import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Separator } from "@/components/ui/separator";
import { Mail, Phone, Users, Calendar as CalendarIcon, CheckCircle2, Clock, Trash2, Send, MessageSquare, Share2, Loader2 } from "lucide-react";
import { API, LEAD_STATUSES } from "@/lib/admin-constants";
import SMSThread from "./SMSThread";
import EmailThread from "./EmailThread";

const LeadDetailSheet = ({ lead, isOpen, onClose, onUpdate, onDelete }) => {
  const [newNote, setNewNote] = useState("");
  const [followUpDate, setFollowUpDate] = useState(null);
  const [followUpDesc, setFollowUpDesc] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [tsTeams, setTsTeams] = useState([]);
  const [tsTeamId, setTsTeamId] = useState("");
  const [tsPushing, setTsPushing] = useState(false);
  const [tsConnected, setTsConnected] = useState(null);
  const [tryouts, setTryouts] = useState([]);
  const [inviteTryoutId, setInviteTryoutId] = useState("");
  const [inviteMsg, setInviteMsg] = useState("");
  const [inviting, setInviting] = useState(false);
  const [showInvite, setShowInvite] = useState(false);

  const openInvite = async () => {
    setShowInvite(true);
    try {
      const res = await axios.get(`${API}/tryouts`, { withCredentials: true });
      setTryouts(res.data.filter(t => t.is_active));
    } catch {
      toast.error("Failed to load tryouts");
    }
  };

  // Fetch TeamSnap connection status & teams once
  useEffect(() => {
    if (!isOpen || tsConnected !== null) return;
    axios.get(`${API}/teamsnap/status`, { withCredentials: true })
      .then((res) => {
        setTsConnected(res.data.connected);
        if (res.data.connected) {
          axios.get(`${API}/teamsnap/teams`, { withCredentials: true })
            .then((r) => setTsTeams(r.data.teams || []))
            .catch(() => {});
        }
      })
      .catch(() => setTsConnected(false));
  }, [isOpen, tsConnected]);

  const handlePushToTeamSnap = async () => {
    const teamId = lead.teamsnap_team_id || parseInt(tsTeamId);
    if (!teamId) { toast.error("Assign a team first"); return; }
    setTsPushing(true);
    try {
      await axios.post(`${API}/teamsnap/sync/push-lead`, { lead_id: lead.lead_id, team_id: teamId }, { withCredentials: true });
      toast.success(`${lead.name} pushed to TeamSnap`);
      if (onUpdate) onUpdate();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to push to TeamSnap"); }
    finally { setTsPushing(false); }
  };

  const sendInvite = async () => {
    if (!inviteTryoutId) { toast.error("Select a tryout"); return; }
    setInviting(true);
    try {
      await axios.post(`${API}/tryouts/invite`, {
        lead_ids: [lead.lead_id],
        tryout_id: inviteTryoutId,
        custom_message: inviteMsg || null,
      }, { withCredentials: true });
      toast.success("Tryout invite sent!");
      setShowInvite(false);
      setInviteTryoutId("");
      setInviteMsg("");
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send invite");
    } finally {
      setInviting(false);
    }
  };

  if (!lead) return null;

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/leads/${lead.lead_id}/notes`, { content: newNote.trim() }, { withCredentials: true });
      setNewNote("");
      toast.success("Note added");
      onUpdate();
    } catch (error) {
      toast.error("Failed to add note");
    }
    setLoading(false);
  };

  const handleAddFollowUp = async () => {
    if (!followUpDate || !followUpDesc.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/leads/${lead.lead_id}/follow-ups`, {
        description: followUpDesc.trim(),
        due_date: format(followUpDate, "yyyy-MM-dd"),
      }, { withCredentials: true });
      setFollowUpDate(null);
      setFollowUpDesc("");
      toast.success("Follow-up scheduled");
      onUpdate();
    } catch (error) {
      toast.error("Failed to schedule follow-up");
    }
    setLoading(false);
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.put(`${API}/leads/${lead.lead_id}/status`, { status: newStatus }, { withCredentials: true });
      toast.success("Status updated");
      onUpdate();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleCompleteFollowUp = async (followUpId) => {
    try {
      await axios.put(`${API}/leads/${lead.lead_id}/follow-ups/${followUpId}`, {}, { withCredentials: true });
      toast.success("Follow-up completed");
      onUpdate();
    } catch (error) {
      toast.error("Failed to complete follow-up");
    }
  };

  const handleDeleteLead = async () => {
    try {
      await axios.delete(`${API}/leads/${lead.lead_id}`, { withCredentials: true });
      toast.success("Lead deleted");
      setConfirmDelete(false);
      onClose();
      if (onDelete) onDelete(lead.lead_id);
    } catch (error) {
      toast.error("Failed to delete lead");
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto p-0" data-testid="lead-detail-sheet">
        {/* Header */}
        <div className="p-5 border-b border-cfc-border">
          <SheetHeader>
            <SheetTitle className="font-heading text-2xl text-cfc-primary">
              {lead.name}
              {lead.lead_type === "coach" && (
                <span className="ml-3 inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-[#0C2E3C] text-[#C8E64E] align-middle">
                  Coach/Team
                </span>
              )}
            </SheetTitle>
            <SheetDescription>
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                LEAD_STATUSES.find((s) => s.id === lead.status)?.color || "bg-gray-500"
              } text-white`}>
                {LEAD_STATUSES.find((s) => s.id === lead.status)?.label || lead.status}
              </span>
              {lead.source && lead.source !== "commonwealth_fc" && (
                <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200" data-testid="lead-source-badge">
                  Source: {lead.source.replace(/_/g, " ").toUpperCase()}
                </span>
              )}
            </SheetDescription>
          </SheetHeader>
        </div>

        {/* Tabbed Content */}
        <Tabs defaultValue="info" className="w-full">
          <TabsList className="w-full justify-start rounded-none border-b bg-transparent h-auto p-0">
            <TabsTrigger value="info" className="rounded-none border-b-2 border-transparent data-[state=active]:border-cfc-primary data-[state=active]:bg-transparent data-[state=active]:text-cfc-primary px-4 py-2.5 text-xs font-bold uppercase" data-testid="lead-tab-info">
              Info
            </TabsTrigger>
            {lead.email && (
              <TabsTrigger value="email" className="rounded-none border-b-2 border-transparent data-[state=active]:border-cfc-primary data-[state=active]:bg-transparent data-[state=active]:text-cfc-primary px-4 py-2.5 text-xs font-bold uppercase" data-testid="lead-tab-email">
                <Mail size={14} className="mr-1.5" /> Email
              </TabsTrigger>
            )}
            {lead.phone && (
              <TabsTrigger value="sms" className="rounded-none border-b-2 border-transparent data-[state=active]:border-cfc-primary data-[state=active]:bg-transparent data-[state=active]:text-cfc-primary px-4 py-2.5 text-xs font-bold uppercase" data-testid="lead-tab-sms">
                <MessageSquare size={14} className="mr-1.5" /> SMS
              </TabsTrigger>
            )}
          </TabsList>

          {/* INFO TAB */}
          <TabsContent value="info" className="p-5 space-y-6 mt-0">
            {/* Contact Info */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-cfc-text-muted">
                <Mail size={16} />
                <a href={`mailto:${lead.email}`} className="hover:text-cfc-primary">{lead.email}</a>
              </div>
              <div className="flex items-center gap-2 text-cfc-text-muted">
                <Phone size={16} />
                <a href={`tel:${lead.phone}`} className="hover:text-cfc-primary">{lead.phone}</a>
              </div>
              <div className="flex items-center gap-2 text-cfc-text-muted">
                <Users size={16} />
                {lead.player_age}
              </div>
              {lead.parent_name && (
                <div className="flex items-center gap-2 text-cfc-text-muted">
                  <Users size={16} />
                  <span>Parent: {lead.parent_name}{lead.parent_email ? ` (${lead.parent_email})` : ""}{lead.parent_phone ? ` — ${lead.parent_phone}` : ""}</span>
                </div>
              )}
              {/* TeamSnap Data */}
              {lead.teamsnap_synced && (
                <div className="mt-3 p-3 bg-indigo-50 rounded border border-indigo-200 space-y-2" data-testid="teamsnap-info">
                  <p className="font-medium text-indigo-800 text-xs mb-2 flex items-center gap-1.5"><Share2 size={12} /> TeamSnap</p>
                  {lead.teamsnap_team_name && (
                    <div className="flex items-center gap-2 text-sm text-indigo-700">
                      <span className="font-medium">Team:</span> {lead.teamsnap_team_name}
                    </div>
                  )}
                  {lead.teamsnap_season && (
                    <div className="flex items-center gap-2 text-sm text-indigo-700">
                      <span className="font-medium">Season:</span> {lead.teamsnap_season}
                    </div>
                  )}
                  {lead.teamsnap_division && (
                    <div className="flex items-center gap-2 text-sm text-indigo-700">
                      <span className="font-medium">Division:</span> {lead.teamsnap_division}
                    </div>
                  )}
                  {(lead.jersey_number || lead.position) && (
                    <div className="flex items-center gap-4 text-sm text-indigo-700">
                      {lead.jersey_number && <span><span className="font-medium">Jersey:</span> #{lead.jersey_number}</span>}
                      {lead.position && <span><span className="font-medium">Position:</span> {lead.position}</span>}
                    </div>
                  )}
                  {lead.teamsnap_contacts && lead.teamsnap_contacts.length > 0 && (
                    <div className="mt-1.5 pt-1.5 border-t border-indigo-200">
                      <p className="text-[10px] font-bold text-indigo-600 uppercase mb-1">Contacts</p>
                      {lead.teamsnap_contacts.map((c, i) => (
                        <div key={i} className="text-xs text-indigo-700 py-0.5">
                          <span className="font-medium">{c.name}</span>
                          {c.relation && <span className="text-indigo-500"> ({c.relation})</span>}
                          {c.email && <span> — {c.email}</span>}
                          {c.phone && <span> — {c.phone}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                  {lead.teamsnap_custom_fields && Object.keys(lead.teamsnap_custom_fields).length > 0 && (
                    <div className="mt-1.5 pt-1.5 border-t border-indigo-200">
                      <p className="text-[10px] font-bold text-indigo-600 uppercase mb-1">Custom Fields</p>
                      {Object.entries(lead.teamsnap_custom_fields).map(([key, value]) => (
                        <div key={key} className="flex items-start gap-2 text-xs text-indigo-700 py-0.5">
                          <span className="font-medium capitalize">{key.replace(/_/g, " ")}:</span>
                          <span>{String(value)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {lead.soccer_experience && (
                <div className="mt-2 p-3 bg-cfc-cream-alt rounded text-sm text-cfc-text-muted">
                  <p className="font-medium text-cfc-primary text-xs mb-1">Soccer Experience</p>
                  {lead.soccer_experience}
                </div>
              )}
              {lead.organization && (
                <div className="flex items-center gap-2 text-cfc-text-muted">
                  <Users size={16} />
                  <span>Organization: {lead.organization}</span>
                </div>
              )}
              {lead.lead_type === "coach" && (
                <div className="mt-3 p-3 bg-[#0C2E3C]/5 rounded border border-[#0C2E3C]/10 space-y-2">
                  <p className="font-medium text-cfc-primary text-xs mb-2">Coach/Team Details</p>
                  {lead.num_players && (
                    <div className="flex items-center gap-2 text-sm text-cfc-text-muted">
                      <span className="font-medium">Players:</span> {lead.num_players}
                    </div>
                  )}
                </div>
              )}
              {lead.message && (
                <div className="mt-2 p-3 bg-cfc-cream-alt rounded text-sm text-cfc-text-muted" data-testid="lead-message">
                  <p className="font-medium text-cfc-primary text-xs mb-1">Message</p>
                  <p>{lead.message}</p>
                </div>
              )}
              {lead.custom_fields && Object.keys(lead.custom_fields).length > 0 && (
                <div className="mt-3 p-3 bg-amber-50 rounded border border-amber-200 space-y-2" data-testid="lead-custom-fields">
                  <p className="font-medium text-cfc-primary text-xs mb-2">Additional Information</p>
                  {Object.entries(lead.custom_fields).map(([key, value]) => (
                    <div key={key} className="flex items-start gap-2 text-sm text-cfc-text-muted">
                      <span className="font-medium min-w-0 shrink-0">{key}:</span>
                      <span className="break-words">{value}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Status Update */}
            <div>
              <Label className="text-cfc-primary font-medium">Update Status</Label>
              <Select value={lead.status} onValueChange={handleStatusChange}>
                <SelectTrigger className="mt-1" data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEAD_STATUSES.map((status) => (
                    <SelectItem key={status.id} value={status.id}>
                      <span className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${status.color}`} />
                        {status.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* TeamSnap Team Assignment */}
            {tsConnected && tsTeams.length > 0 && (
              <div>
                <Label className="text-cfc-primary font-medium">TeamSnap Team</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Select
                    value={lead.teamsnap_team_id ? String(lead.teamsnap_team_id) : (tsTeamId || "unassigned")}
                    onValueChange={async (val) => {
                      if (val === "unassigned") {
                        // Clear team assignment
                        try {
                          await axios.put(`${API}/leads/${lead.lead_id}`, { teamsnap_team_id: null, teamsnap_team_name: "", teamsnap_synced: false }, { withCredentials: true });
                          toast.success("Team assignment cleared");
                          if (onUpdate) onUpdate();
                        } catch { toast.error("Failed to update"); }
                        return;
                      }
                      const team = tsTeams.find((t) => String(t.id) === val);
                      try {
                        await axios.put(`${API}/leads/${lead.lead_id}`, {
                          teamsnap_team_id: parseInt(val),
                          teamsnap_team_name: team?.name || "",
                          teamsnap_season: team?.season_name || "",
                          teamsnap_division: team?.division_name || "",
                        }, { withCredentials: true });
                        setTsTeamId(val);
                        toast.success(`Assigned to ${team?.name || "team"}`);
                        if (onUpdate) onUpdate();
                      } catch { toast.error("Failed to assign team"); }
                    }}
                  >
                    <SelectTrigger className="text-sm flex-1" data-testid="teamsnap-team-assign">
                      <SelectValue placeholder="Assign a team..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">— No Team —</SelectItem>
                      {tsTeams.map((t) => (
                        <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {lead.teamsnap_team_id && !lead.teamsnap_synced && (
                    <Button size="sm" className="bg-cfc-primary text-white" onClick={handlePushToTeamSnap} disabled={tsPushing} data-testid="push-teamsnap-btn">
                      {tsPushing ? <Loader2 size={14} className="animate-spin" /> : <Share2 size={14} />}
                    </Button>
                  )}
                  {lead.teamsnap_synced && (
                    <span className="text-[10px] font-bold text-green-600 bg-green-100 px-2 py-1 rounded whitespace-nowrap">Synced</span>
                  )}
                </div>
                <p className="text-[10px] text-cfc-text-muted mt-1">
                  {lead.teamsnap_synced
                    ? "This player is synced to TeamSnap. Changes sync automatically."
                    : lead.teamsnap_team_id
                    ? "Team assigned — click the push button or wait for next auto-sync."
                    : "Assign a team to enable TeamSnap sync."}
                </p>
              </div>
            )}

            {/* Invite to Tryout */}
            <div>
              {!showInvite ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full border-cfc-primary/30 text-cfc-primary hover:bg-cfc-primary/5"
                  onClick={openInvite}
                  data-testid="invite-tryout-btn"
                >
                  <Send size={14} className="mr-2" /> Invite to Tryout
                </Button>
              ) : (
                <div className="border border-cfc-border rounded-lg p-3 space-y-2 bg-cfc-cream/30" data-testid="invite-tryout-form">
                  <Label className="text-cfc-primary font-medium text-xs">Select Tryout</Label>
                  <Select value={inviteTryoutId} onValueChange={setInviteTryoutId}>
                    <SelectTrigger className="text-sm" data-testid="invite-tryout-select">
                      <SelectValue placeholder="Choose a tryout..." />
                    </SelectTrigger>
                    <SelectContent>
                      {tryouts.map(t => (
                        <SelectItem key={t.tryout_id} value={t.tryout_id}>
                          {t.title} — {t.date}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Textarea
                    value={inviteMsg}
                    onChange={(e) => setInviteMsg(e.target.value)}
                    placeholder="Optional personal message..."
                    className="text-sm min-h-[50px]"
                    data-testid="invite-message-input"
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={sendInvite}
                      disabled={inviting || !inviteTryoutId}
                      className="bg-cfc-primary hover:bg-cfc-primary-hover flex-1"
                      data-testid="send-invite-btn"
                    >
                      <Send size={13} className="mr-1" /> {inviting ? "Sending..." : "Send Invite"}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setShowInvite(false)}>Cancel</Button>
                  </div>
                </div>
              )}
            </div>

            <Separator />

            {/* Notes */}
            <div>
              <Label className="text-cfc-primary font-medium mb-2 block">Notes</Label>
              <div className="space-y-2 mb-3 max-h-40 overflow-y-auto">
                {lead.notes?.length > 0 ? (
                  lead.notes.map((note, index) => (
                    <div key={note.note_id || index} className="bg-cfc-cream p-3 rounded-sm text-sm">
                      <p className="text-cfc-primary">{note.content}</p>
                      <p className="text-xs text-cfc-text-muted mt-1">
                        {note.created_by} - {new Date(note.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-cfc-text-muted">No notes yet</p>
                )}
              </div>
              <div className="flex gap-2">
                <Textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Add a note..."
                  className="text-sm"
                  data-testid="note-input"
                />
              </div>
              <Button
                size="sm"
                className="mt-2 bg-cfc-primary hover:bg-cfc-primary-hover"
                onClick={handleAddNote}
                disabled={loading || !newNote.trim()}
                data-testid="add-note-btn"
              >
                Add Note
              </Button>
            </div>

            {/* Follow-ups */}
            <div>
              <Label className="text-cfc-primary font-medium mb-2 block">Follow-ups</Label>
              <div className="space-y-2 mb-3 max-h-40 overflow-y-auto">
                {lead.follow_ups?.length > 0 ? (
                  lead.follow_ups.map((fu, index) => (
                    <div
                      key={fu.follow_up_id || index}
                      className={`p-3 rounded-sm text-sm border ${
                        fu.completed ? "bg-green-50 border-green-200" : "bg-cfc-cream border-cfc-border"
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-cfc-primary font-medium">{fu.description}</p>
                          <p className="text-xs text-cfc-text-muted mt-1 flex items-center gap-1">
                            <CalendarIcon size={12} />
                            Due: {fu.due_date}
                          </p>
                        </div>
                        {!fu.completed && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleCompleteFollowUp(fu.follow_up_id)}
                            className="text-green-600 hover:text-green-700"
                          >
                            <CheckCircle2 size={18} />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-cfc-text-muted">No follow-ups scheduled</p>
                )}
              </div>

              <div className="space-y-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start" data-testid="followup-date-btn">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {followUpDate ? format(followUpDate, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar mode="single" selected={followUpDate} onSelect={setFollowUpDate} initialFocus />
                  </PopoverContent>
                </Popover>
                <Input
                  value={followUpDesc}
                  onChange={(e) => setFollowUpDesc(e.target.value)}
                  placeholder="Follow-up description..."
                  data-testid="followup-desc-input"
                />
                <Button
                  size="sm"
                  className="bg-cfc-accent hover:bg-cfc-accent-hover text-cfc-primary"
                  onClick={handleAddFollowUp}
                  disabled={loading || !followUpDate || !followUpDesc.trim()}
                  data-testid="add-followup-btn"
                >
                  <Clock size={16} className="mr-2" />
                  Schedule Follow-up
                </Button>
              </div>
            </div>

            {/* Delete Lead */}
            <div className="pt-4 border-t border-cfc-border">
              {!confirmDelete ? (
                <Button
                  variant="outline"
                  className="w-full text-red-500 border-red-200 hover:bg-red-50 hover:text-red-600"
                  onClick={() => setConfirmDelete(true)}
                  data-testid="delete-lead-btn"
                >
                  <Trash2 size={16} className="mr-2" />
                  Delete Lead
                </Button>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-red-600 font-medium">Permanently delete this lead and all their data?</p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1"
                      onClick={() => setConfirmDelete(false)}
                      data-testid="cancel-delete-lead-btn"
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                      onClick={handleDeleteLead}
                      data-testid="confirm-delete-lead-btn"
                    >
                      <Trash2 size={14} className="mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* EMAIL TAB */}
          {lead.email && (
            <TabsContent value="email" className="p-5 mt-0">
              <EmailThread leadId={lead.lead_id} />
            </TabsContent>
          )}

          {/* SMS TAB */}
          {lead.phone && (
            <TabsContent value="sms" className="p-5 mt-0">
              <SMSThread leadId={lead.lead_id} />
            </TabsContent>
          )}
        </Tabs>
      </SheetContent>
    </Sheet>
  );
};

export default LeadDetailSheet;
